/* eslint-disable @next/next/no-img-element */

import React, { useEffect, useMemo, useState } from "react";
import Head from "next/head";

import Backend from "../../../utils/Backend";
import categories from "../../../helper/categories";
import handleTimeout from "../../../helper/timeout_handler";
import { formatDate } from "../../../helper/date_formatter";
import { videoViewEvent } from "../../../utils/Analytics";

import Footer from "../../../components/Footer";
import Header from "../../../components/Header";
import RichText from "../../../components/RichText";
import GoogleAd from "../../../components/GoogleAd";
import MoreLearnContent from "../../../components/MoreLearnContent";
import KeepBrowsingVehicles from "../../../components/KeepBrowsingVehicles";
import YoutubeEmbed from "../../../components/YouTubeEmbed";
import Tags from "../../../components/Tags";
import { googleAdIds, links, video_view } from "../../../helper/constants";
import { ITag } from "../../../interfaces/ITag";
import { useIsBoiWebsite, } from "../../../components/Context/WebsiteType";
import { WebsiteTheme } from "../../../helper/enums/website_types";

export default function VideoPage({ video }: any) {
	const [startedWatching, setStartedWatching] = useState(false);
	const [watched, setWatched] = useState(false);
	const [analyticsID, setAnalyticsID] = useState<string>();

	const [loadingVideo, setLoadingVideo] = useState(true);

	const isBoiWebsite = useIsBoiWebsite();

	useEffect(() => {
		if (!loadingVideo) return;

		setTimeout(() => {
			setLoadingVideo(false);
		}, 500);
	}, [loadingVideo]);

	// Checking startWatching & watched statuses, and analytics id
	useEffect(() => {
		setStartedWatching(!!sessionStorage.getItem(`${video_view.started_watching}-${video.id}`));
		setWatched(!!sessionStorage.getItem(`${video_view.watched}-${video.id}`));
		setAnalyticsID(sessionStorage.getItem(`${video_view.analytics_id}-${video.id}`) || undefined);
	}, [video]);

	// Analytics for video start
	const handleVideoStart = async () => {
		if (!startedWatching) {
			const analytics_id = await videoViewEvent(isBoiWebsite, video.id, false);

			// storing analytics id in order to update the video watched status
			if (analytics_id) {
				setAnalyticsID(`${analytics_id}`);
				sessionStorage.setItem(`${video_view.analytics_id}-${video.id}`, `${analytics_id}`);
			}

			sessionStorage.setItem(`${video_view.started_watching}-${video.id}`, "true");
			setStartedWatching(true);
		}
	};

	// Analytics for video watched
	const handleVideoWatched = () => {
		if (!watched && analyticsID) {
			videoViewEvent(isBoiWebsite, video.id, true, analyticsID);
			sessionStorage.setItem(`${video_view.watched}-${video.id}`, "true");
			setWatched(true);
		}
	};

	const hiddenTags = useMemo(() => {
		let tags = "";

		video.tags.forEach((tag: ITag) => {
			if (!tag.visible) {
				tags += `${tag.name}, `;
			}
		});

		if (tags.length) tags = tags.slice(0, -2);

		return tags;
	}, [video]);

	const preRollURL = useMemo(() => {
		return video?.pre_roll_video?.video_url ? (video.pre_roll_video.video_url as string) : undefined;
	}, [video]);

	return (
		<>
			{video && (
				<Head>
					<title>
						{video.title} - {categories.articles} | Nevo
					</title>
					<meta name="author" content={video.author || "Nevo"} />
					<meta name="description" content={video.page_metadata} />
					<meta name="keywords" content={hiddenTags} />
				</Head>
			)}

			<Header transparent={isBoiWebsite} />

			{video && (
				<div className={"page-holder post-page-holder"}>
					{!isBoiWebsite && <GoogleAd adId={googleAdIds.horizontal_banner} className="ad-banner-post" videoPage />}

					<div className="post-holder learn-post-holder">
						<p className={`post-type ${isBoiWebsite ? WebsiteTheme.boi : WebsiteTheme.nevo}`}>Video Review</p>

						<h1 className="post-title">{video.title}</h1>
						<p className="post-meta">
							By {video.author || "Nevo"} • {formatDate(video.created_at)}
						</p>

						<Tags tags={video.tags} base_url={links.videos} className="post-tags" link isBoiWebsite={isBoiWebsite} isArticle={false}/>

						{!loadingVideo ? (
							<YoutubeEmbed
								playVideo
								videoURL={video.video_url}
								preRollURL={preRollURL}
								isPreroll={!!preRollURL}
								onPage
								onPageClassName="post-video"
								autoPlay={false}
								startedWatching={startedWatching}
								watchedVideo={watched}
								handleVideoStart={handleVideoStart}
								handleVideoWatched={handleVideoWatched}
							/>
						) : (
							<div className="post-video youtube-video">&nbsp;</div>
						)}

						<RichText content={video.content} />
					</div>

					{/* <MoreLearnContent container /> */}
				</div>
			)}

			<KeepBrowsingVehicles />

			<Footer />
		</>
	);
}

export async function getServerSideProps({ res, params: { slug } }: any) {
	// params contains the vehicle `id`
	res.setHeader("Cache-Control", "public, s-maxage=10, stale-while-revalidate=59");

	const response = await handleTimeout(Backend.getVideoBySlug(slug), `Video ${slug}`);

	if (!response) {
		throw new Error("Failed to fetch Video");
	}

	const video = response.data.content;

	// Pass data to the page via props
	return {
		props: { video }
	};
}
